import { Badge, Box, Divider, Fade, IconButton, Tooltip } from '@mui/material';
import { Pin } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import { useRecoilState, useRecoilValue } from 'recoil';
import { labelWithHotkey } from '~/components/toolbar/shared/toolEntry';
import ToolbarButton from '~/components/toolbar/shared/ToolbarButton';
import ToolHoverTray from '~/components/toolbar/shared/ToolHoverTray';
import { useViewingToolEntries } from '~/components/toolbar/shared/useViewingToolEntries';
import { useRegionRefusal } from '~/hooks/useRegionRefusal';
import { useMeasureMode } from '~/hooks/useMeasureMode';
import { useMeasureShortcuts } from '~/hooks/viewer/useMeasureShortcuts';
import { selectedAnnotationState } from '~/state/atoms/annotationListAtoms';
import {
    intensityShapeState,
    lineProfileDraftState,
    lineProfileListState,
} from '~/state/atoms/intensityToolAtoms';
import {
    measureTrayPinnedState,
    placedAnnotationCountState,
} from '~/state/atoms/measurementAtoms';
import MeasureAnalysis from './MeasureAnalysis';
import MeasureFooter from './MeasureFooter';
import MeasureModePicker from './MeasureModePicker';

/** Hover delays, ms: a passing pointer does not flash the tray open. */
const OPEN_DELAY_MS = 400;
const CLOSE_DELAY_MS = 150;

/**
 * Toolbar entry for the Measure tool. A click resumes the last mode; the badge
 * counts the shapes placed. The tray is open while hovered, while keyboard
 * focus is in it and while pinned. While measuring it is also held open for a
 * profile being drawn or selected, so the profile can be read as it is drawn
 * and once it is placed.
 *
 * @param allowRegions false on the dose step, where a region's figures could
 *   be read as a dose statistic (SR-WOR-H-17-5).
 */
export default function MeasureTool({
    allowRegions = true,
}: {
    allowRegions?: boolean;
}) {
    const intl = useIntl();
    const t = (id: string) => intl.formatMessage({ id });
    const { active, select } = useMeasureMode();
    const { measurement } = useViewingToolEntries();
    const count = useRecoilValue(placedAnnotationCountState);
    const [pinned, setPinned] = useRecoilState(measureTrayPinnedState);
    const placingProfile = useRecoilValue(lineProfileDraftState) !== null;
    // Ids rather than the selected record, which changes on every drag.
    const selectedId = useRecoilValue(selectedAnnotationState);
    const profileIds = useRecoilValue(lineProfileListState.ids);
    const profileSelected =
        selectedId !== null && profileIds.includes(selectedId);
    const holdingProfile =
        active !== null && (placingProfile || profileSelected);

    // Here rather than in the tray, which is not always mounted: the engine
    // would arm a refused region from the stored shape.
    const regionRefusal = useRegionRefusal(allowRegions);
    const [shape, setShape] = useRecoilState(intensityShapeState);
    useEffect(() => {
        if (regionRefusal !== undefined && shape !== 'line') {
            setShape('line');
        }
    }, [regionRefusal, shape, setShape]);

    useMeasureShortcuts({ active, regionRefusal, select });

    const [hovered, setHovered] = useState(false);
    const [focused, setFocused] = useState(false);
    const open = hovered || focused || pinned || holdingProfile;
    const trayShown = useRef(open);
    useEffect(() => {
        trayShown.current = open;
    });

    // Read from document pointer moves: Chromium skips mouseleave when a
    // click re-renders the element under the pointer. The wrapper's handler
    // runs first and marks moves inside it, its portalled tooltips included.
    const insideMove = useRef<Event>();
    useEffect(() => {
        let inside = false;
        let timer: ReturnType<typeof setTimeout> | undefined;
        const onPointerMove = (event: PointerEvent): void => {
            if ((event === insideMove.current) === inside) {
                return;
            }
            inside = !inside;
            clearTimeout(timer);
            // Over a tray already shown it counts at once, so a click that
            // drops another reason (unpinning) does not close it underfoot.
            if (inside && trayShown.current) {
                setHovered(true);
                return;
            }
            timer = setTimeout(
                () => setHovered(inside),
                inside ? OPEN_DELAY_MS : CLOSE_DELAY_MS
            );
        };
        document.addEventListener('pointermove', onPointerMove);
        return () => {
            document.removeEventListener('pointermove', onPointerMove);
            clearTimeout(timer);
        };
    }, []);

    return (
        <Box
            onPointerMove={(event) => {
                insideMove.current = event.nativeEvent;
            }}
            // Focus from a click does not open it, or the tray would stay
            // over the image after the pointer left.
            onFocus={(event) =>
                event.target.matches(':focus-visible') && setFocused(true)
            }
            onBlur={(event) =>
                !event.currentTarget.contains(event.relatedTarget) &&
                setFocused(false)
            }
            position="relative"
        >
            <Badge
                badgeContent={count}
                color="primary"
                sx={{
                    '& .MuiBadge-badge': {
                        fontSize: '0.6rem',
                        minWidth: 16,
                        height: 16,
                        px: 0.5,
                        pointerEvents: 'none',
                    },
                }}
            >
                <ToolbarButton
                    icon={measurement.icon}
                    tooltip={labelWithHotkey(measurement)}
                    ariaLabel={measurement.label}
                    selected={measurement.selected}
                    ariaPressed={measurement.selected}
                    onClick={measurement.toggle}
                    data-testid="measure-tool-button"
                />
            </Badge>
            <Fade in={open} timeout={{ enter: 200, exit: 150 }} unmountOnExit>
                <ToolHoverTray
                    data-testid="measure-tray"
                    sx={{
                        width: 340,
                        p: 0,
                        display: 'block',
                        whiteSpace: 'normal',
                        borderRadius: '8px',
                    }}
                >
                    <MeasureModePicker
                        active={active}
                        onSelect={select}
                        regionRefusal={regionRefusal}
                    />
                    {/* Sideways, out of the tray: below it is the rectangle mode. */}
                    <Tooltip
                        title={t(pinned ? 'measure.unpin' : 'measure.pin')}
                        placement="right"
                    >
                        <IconButton
                            size="small"
                            aria-label={t('measure.pin')}
                            aria-pressed={pinned}
                            onClick={() => setPinned((wasPinned) => !wasPinned)}
                            data-testid="measure-pin"
                            sx={{
                                position: 'absolute',
                                top: 6,
                                right: 6,
                                p: 0.5,
                                color: pinned
                                    ? 'primary.main'
                                    : 'text.secondary',
                            }}
                        >
                            <Pin
                                size={14}
                                fill={pinned ? 'currentColor' : 'none'}
                            />
                        </IconButton>
                    </Tooltip>
                    <Divider />
                    {/* Scrolls on its own, keeping the footer on screen. */}
                    <Box px={1.5} py={1} maxHeight="55vh" overflow="auto">
                        <MeasureAnalysis />
                    </Box>
                    <MeasureFooter active={active} count={count} />
                </ToolHoverTray>
            </Fade>
        </Box>
    );
}
